import uniqBy from 'lodash/uniqBy';
import type { ApolloError } from 'apollo-client';
import type { Action, StoreActionApi } from 'react-sweet-state';
import { INITIAL_STATE } from '../constants';
import type { EpicCardType, BacklogEpicsStoreInitProps, State } from '../types';

export type InitContainerAction = () => Action<State, BacklogEpicsStoreInitProps, void>;

export const initContainer =
	() =>
	({ setState }: StoreActionApi<State>, initProps: BacklogEpicsStoreInitProps) => {
		setState({
			...initProps,
			isInitialised: true,
		});
	};

export const initState =
	(initProps: BacklogEpicsStoreInitProps) =>
	({ setState }: StoreActionApi<State>) => {
		setState({
			...initProps,
			isInitialised: true,
		});
	};

export const resetState =
	() =>
	({ setState }: StoreActionApi<State>) => {
		setState(INITIAL_STATE);
	};

export const updateQueryState =
	(newQueryState: { loading?: boolean; error?: ApolloError; refetchError?: ApolloError } = {}) =>
	({ setState }: StoreActionApi<State>) => {
		setState({
			...newQueryState,
		});
	};

export const addEpics =
	(epics: EpicCardType[], optimisticIds: number[] = []) =>
	({ setState, getState }: StoreActionApi<State>) => {
		// There is an implicit assumption that the optimisticIds match up 1-1 with the epics provided
		const { cards } = getState();

		if (!optimisticIds.length) {
			setState({
				cards: [...cards, ...epics],
			});
			return;
		}

		const firstIndex = cards.findIndex(({ epic: { id } }) => optimisticIds[0] === id);
		const lastIndex = cards.findIndex(
			({ epic: { id } }) => optimisticIds[optimisticIds.length - 1] === id,
		);

		setState({
			cards: [...cards.slice(0, firstIndex), ...epics, ...cards.slice(lastIndex + 1)],
		});
	};

export const rankEpics =
	(epicIds: number[], newPosition: number) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards: existingCards } = getState();

		const cardsNotMoved = existingCards.filter(({ epic: { id } }) => !epicIds.includes(Number(id)));
		const cardsMoved = existingCards.filter(({ epic: { id } }) => epicIds.includes(Number(id)));

		setState({
			cards: [
				...cardsNotMoved.slice(0, newPosition),
				...cardsMoved,
				...cardsNotMoved.slice(newPosition),
			],
		});
	};

export const updateEpics =
	({
		updatedCards,
		startIndex = 0,
		hasNextPage,
	}: {
		updatedCards: EpicCardType[];
		startIndex?: number;
		hasNextPage: boolean;
	}) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards } = getState();

		cards.splice(
			startIndex,
			hasNextPage ? updatedCards.length : cards.length - startIndex,
			...updatedCards,
		);

		setState({
			cards: uniqBy(cards, (card) => card.epic.id),
		});
	};

export const updateEpicAttributes =
	(epicId: string, attributes: Partial<EpicCardType['epic']>) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards } = getState();
		const updatedCards = cards.map((card) => {
			if (card.epic.id === Number(epicId)) {
				return {
					...card,
					epic: {
						...card.epic,
						...attributes,
					},
				};
			}
			return card;
		});

		setState({
			cards: updatedCards,
		});
	};

export const updateEpicsAttributes =
	(attributes: Partial<EpicCardType['epic']>, startIndex = 0) =>
	({ setState, getState }: StoreActionApi<State>) => {
		const { cards: existingCards } = getState();

		const cards = existingCards.map((card, index) => ({
			...card,
			epic: {
				...card.epic,
				...(index >= startIndex && attributes),
			},
		}));

		setState({
			cards,
		});
	};

export type Actions = {
	initContainer: InitContainerAction;
	initState: typeof initState;
	resetState: typeof resetState;
	updateQueryState: typeof updateQueryState;
	addEpics: typeof addEpics;
	rankEpics: typeof rankEpics;
	updateEpics: typeof updateEpics;
	updateEpicAttributes: typeof updateEpicAttributes;
	updateEpicsAttributes: typeof updateEpicsAttributes;
};

export const actions: Actions = {
	initContainer,
	initState,
	resetState,
	updateQueryState,
	addEpics,
	rankEpics,
	updateEpics,
	updateEpicAttributes,
	updateEpicsAttributes,
};
